// 🔗 명세서 공유 링크 열람 페이지 (비로그인) — /s/:token
// 비회원 치과가 가입 없이 명세서를 열람하고 [확인했습니다]를 누를 수 있는 페이지.
// 하단에 가입 유도 배너(기공소 초대 코드 연결). 데이터는 statementShares/{token} 스냅샷.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { STATEMENT_VAT_NOTE } from '../utils/statementView';
import { remakeOfLabel } from '../utils/statementItems';   // 재제작 '처음 작업' 글자 (W2 공용)
import CarryOverBox from './statement/CarryOverBox';   // 전월 미수 안내 상자 (2026-10-23 1단계 §4-5)

const fmtWon = (n) => (Number(n) || 0).toLocaleString();
const toDate = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d) ? null : d;
};
// 법무 고정 문구 (계약 §2, 2026-10-24) — 글자 바꾸지 말 것
const CONFIRM_LEGAL_NOTE = '‘확인했습니다’는 명세서 내용을 보았다는 표시입니다. 대금 정산 확정이나 금액 동의를 뜻하지 않습니다. 금액·내역이 다르면 [수정 요청]을 눌러 주세요.';
const inputLegalNote = (labName) => `입력하신 이름과 내용은 ${labName || '기공소'}에 전달되고 링크 보관 기간 동안 저장됩니다. 환자 이름 전체·주민등록번호·연락처는 적지 마세요(예: "10/21 김O수 #36 금액 확인 부탁드립니다").`;
// 수정 요청 제한 — rules(계약 §3)와 같은 값: 글 1~200자, 이름 ≤30자, 링크당 최대 3회(확인 전까지)
const REV_MAX = 3;
const REV_TEXT_MAX = 200;
const NAME_MAX = 30;
// 수정 요청 칸 rev1~rev3 (배열 아님 — rules 계약 §3)
const revisionsOf = (share) => [1, 2, 3].map(n => share?.[`rev${n}`]).filter(r => r && typeof r.text === 'string');
const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d)) return '';
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

function StatementShareView() {
    const { token } = useParams();
    const navigate = useNavigate();
    // undefined=로딩, null=없음/만료(rules가 만료·없는 문서 get을 같은 permission-denied로 거부 — 구분 불가), 'expired'=문서는 읽혔지만 기간 지남, 'error'=통신 오류
    const [share, setShare] = useState(undefined);
    const [reloadKey, setReloadKey] = useState(0);
    const [confirming, setConfirming] = useState(false);
    const [confirmName, setConfirmName] = useState('');   // 확인자명 인라인 입력 (window.prompt 대체, 2026-08-26)
    // 수정 요청 (2026-10-24 2단계 ⑥) — 비회원 치과: 글 200자, 최대 3회, 확인 전까지만
    const [revOpen, setRevOpen] = useState(false);
    const [revText, setRevText] = useState('');
    const [revName, setRevName] = useState('');   // 수정 요청 보내는 분 성함(선택) — 비우면 위 '확인하시는 분 성함' 칸 값 (2026-10-24 재점검 UX)
    const [sendingRev, setSendingRev] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'statementShares', token));
                if (!alive) return;
                if (!snap.exists()) { setShare(null); return; }
                const data = { id: snap.id, ...snap.data() };
                // rules 배포 전·시계 차이 대비 — 화면에서도 기간을 본다
                const exp = toDate(data.expiresAt);
                setShare(exp && exp.getTime() <= Date.now() ? 'expired' : data);
            } catch (e) {
                // 토큰·환자 정보는 로그에 남기지 않는다 — 코드만
                console.error('공유 명세서 로드 실패:', e?.code || e?.message);
                if (!alive) return;
                setShare(e?.code === 'permission-denied' || e?.code === 'not-found' ? null : 'error');
            }
        })();
        return () => { alive = false; };
    }, [token, reloadKey]);

    const handleConfirm = async () => {
        if (confirming || sendingRev) return;   // 이중 클릭 방지
        // 보안 P0-3 (2026-10-23): 30자 제한 — rules도 30자 초과를 거부함
        const name = String(confirmName || '').trim().slice(0, NAME_MAX) || '치과 담당자';
        setConfirming(true);
        try {
            // rules(계약 §3): confirmedAt·updatedAt == request.time → 서버 시각
            await updateDoc(doc(db, 'statementShares', token), {
                confirmedAt: serverTimestamp(),
                confirmedName: name,
                updatedAt: serverTimestamp(),
            });
            setShare(prev => ({ ...prev, confirmedAt: Timestamp.now(), confirmedName: name }));
            alert('확인되었습니다. 기공소에 전달됩니다. 감사합니다!');
        } catch (e) {
            console.error('확인 처리 실패:', e?.code || e?.message);
            alert(`확인이 처리되지 않았습니다.\n이미 확인을 눌렀거나 링크 기간이 지난 경우입니다. 화면을 새로고침해도 같다면 ${share?.labName || '기공소'}에 전화로 알려 주세요.`);
        } finally {
            setConfirming(false);
        }
    };

    const handleSendRevision = async () => {
        if (sendingRev || confirming || !share || typeof share !== 'object') return;   // 이중 클릭 방지
        const text = String(revText || '').trim();
        if (!text) { alert('수정 요청 내용을 적어 주세요.'); return; }
        if (text.length > REV_TEXT_MAX) { alert(`수정 요청은 ${REV_TEXT_MAX}자까지 적을 수 있습니다.`); return; }
        const n = Number(share.revisionCount) || 0;
        if (n >= REV_MAX) { alert(`수정 요청은 최대 ${REV_MAX}번까지 보낼 수 있습니다. 기공소에 직접 연락해 주세요.`); return; }
        const name = (String(revName || '').trim() || String(confirmName || '').trim()).slice(0, NAME_MAX);
        const key = `rev${n + 1}`;   // rules: 키는 [rev{n+1}, revisionCount, updatedAt]만
        const entry = { text, ...(name ? { name } : {}), at: serverTimestamp() };
        setSendingRev(true);
        try {
            await updateDoc(doc(db, 'statementShares', token), {
                [key]: entry,
                revisionCount: n + 1,
                updatedAt: serverTimestamp(),
            });
            setShare(prev => ({ ...prev, [key]: { ...entry, at: Timestamp.now() }, revisionCount: n + 1 }));
            setRevText('');
            setRevOpen(false);
            alert('수정 요청을 보냈습니다. 기공소에 전달됩니다.');
        } catch (e) {
            console.error('수정 요청 실패:', e?.code || e?.message);   // 글 원문은 남기지 않는다
            alert(`수정 요청을 보내지 못했습니다.\n이미 확인을 눌렀거나, ${REV_MAX}번을 다 썼거나, 링크 기간이 지난 경우입니다. ${share?.labName || '기공소'}에 직접 연락해 주세요.`);
        } finally {
            setSendingRev(false);
        }
    };

    if (share === undefined) {
        return <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94A3B8', fontFamily: 'sans-serif' }}>명세서를 불러오는 중...</div>;
    }
    if (share === 'error') {
        return (
            <div style={{ textAlign: 'center', padding: '80px 20px', fontFamily: 'sans-serif' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#B91C1C' }}>{'명세서를 불러오지 못했습니다'}</div>
                <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 6 }}>{'인터넷 연결을 확인한 뒤 다시 시도해 주세요.'}</div>
                <button onClick={() => { setShare(undefined); setReloadKey(k => k + 1); }}
                    style={{ marginTop: 14, padding: '8px 18px', background: '#fff', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    {'다시 시도'}
                </button>
            </div>
        );
    }
    if (share === null || share === 'expired') {
        // 만료 링크는 rules가 읽기 자체를 막아 '없음'과 구분되지 않는다 → 같은 안내 (명세 §4-2 S-3)
        return (
            <div style={{ textAlign: 'center', padding: '80px 20px', fontFamily: 'sans-serif' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1E293B' }}>
                    {share === 'expired' ? '링크 기간이 지났습니다' : '명세서를 열 수 없습니다'}
                </div>
                <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 6 }}>
                    {share === 'expired'
                        ? '기공소에 새 링크를 요청하세요.'
                        : '링크 기간이 지났거나 삭제된 링크입니다. 기공소에 새 링크를 요청하세요.'}
                </div>
            </div>
        );
    }

    const s = share.snapshot || {};
    const items = s.items || [];
    const confirmed = !!share.confirmedAt;
    const revisions = revisionsOf(share);
    const revCount = Math.max(Number(share.revisionCount) || 0, revisions.length);
    const canRevise = !confirmed && revCount < REV_MAX;
    const expiresText = fmtDate(share.expiresAt);   // 연도 포함 (2026-10-24 재점검 UX — 연말·연초 링크가 'M/D'만으로는 헷갈림)

    return (
        <div style={{ background: '#F1F5F9', minHeight: '100vh', padding: '24px 12px', fontFamily: '-apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", sans-serif' }}>
            <div style={{ maxWidth: 760, margin: '0 auto' }}>
                {/* 상단 브랜드 */}
                <div style={{ textAlign: 'center', marginBottom: 14, fontSize: 13, color: '#64748B' }}>
                    🦷 <b style={{ color: '#4338CA' }}>덴탈클러스터</b> 거래명세서
                </div>

                {/* 명세서 카드 */}
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '28px 24px', boxShadow: '0 4px 20px rgba(15,23,42,0.06)' }}>
                    <h1 style={{ textAlign: 'center', fontSize: 21, fontWeight: 800, letterSpacing: 3, margin: '0 0 18px' }}>거 래 명 세 서</h1>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, fontSize: 13, color: '#475569', borderTop: '2px solid #1E293B', borderBottom: '1px solid #E2E8F0', padding: '10px 2px', marginBottom: 4 }}>
                        <span><b>공급자(기공소)</b> {share.labName || '-'}</span>
                        <span><b>공급받는자(치과)</b> {share.clinicName || '-'}</span>
                        <span><b>명세서번호</b> <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.statementNumber || '-'}</span></span>
                        {(s.periodStart || s.periodEnd) && <span><b>기간</b> {fmtDate(s.periodStart)} ~ {fmtDate(s.periodEnd)}</span>}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
                            <thead>
                                <tr style={{ background: '#F8FAFC', color: '#475569' }}>
                                    {['날짜', '환자', '내역', '수량', '단가', '금액'].map(h => (
                                        <th key={h} style={{ padding: '8px 6px', borderBottom: '1px solid #E2E8F0', textAlign: h === '내역' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9', textAlign: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{it.date || ''}</td>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9', textAlign: 'center', whiteSpace: 'nowrap' }}>{it.patientName || ''}</td>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9' }}>
                                            {it.description || ''}
                                            {/* 재제작 '처음 작업' 표시 (계약 §5 — 날짜·번호 글자만. 책임 구분(remakeLiability)은 서버가 스냅샷에 넣지 않고 여기서도 읽지 않음) */}
                                            {remakeOfLabel(it) && (
                                                <span style={{ marginLeft: 6, fontSize: 11.5, color: '#B45309' }}>{`(${remakeOfLabel(it)})`}</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{it.quantity || ''}</td>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.unitPrice ? fmtWon(it.unitPrice) : ''}</td>
                                        <td style={{ padding: '7px 6px', borderBottom: '1px solid #F1F5F9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtWon(it.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* 이름 가림 안내 · 유효 기간 (명세 §4-4) — 이름은 서버가 가린 값을 그대로 보여 준다 */}
                    <div style={{ marginTop: 8, fontSize: 12, color: '#64748B' }}>
                        {'※ 환자 이름은 일부 가려져 있습니다. 날짜·치식으로 확인해 주세요.'}
                        {expiresText ? ` · 링크 유효: ${expiresText}까지` : ''}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginTop: 14, fontSize: 13.5 }}>
                        {Number(s.discountAmount) > 0 && (<>
                            <span style={{ color: '#64748B' }}>소계 {fmtWon(s.subtotal)}원</span>
                            <span style={{ color: '#DC2626' }}>할인 -{fmtWon(s.discountAmount)}원</span>
                        </>)}
                        <span style={{ fontSize: 17, fontWeight: 800 }}>합계 {fmtWon(s.totalAmount)}원</span>
                    </div>
                    {/* 부가세 안내 — 법적보안 확정 A안·대표 승인 2026-10-06 (상세·미리보기 양식과 같은 문구, utils/statementView STATEMENT_VAT_NOTE) */}
                    {STATEMENT_VAT_NOTE && <div style={{ fontSize: 12, color: '#64748B', textAlign: 'right', marginTop: 4 }}>{STATEMENT_VAT_NOTE}</div>}
                    {/* 전월 미수 안내 (2026-10-23) — 공유 스냅샷에 고정된 값. 합계에 더하지 않음 */}
                    <CarryOverBox carryOver={s.carryOver} labName={share.labName} />
                    {/* 이체 계좌 (스냅샷에 있을 때만 — 2026-08-26) */}
                    {s.bankAccount?.accountNumber && (
                        <div style={{ marginTop: 12, padding: '10px 12px', background: '#EEF2FF', borderRadius: 8, fontSize: 13, textAlign: 'right' }}>
                            <span style={{ fontSize: 11.5, color: '#4F46E5', fontWeight: 700, marginRight: 8 }}>이체 계좌</span>
                            <b>{s.bankAccount.bankName || ''}</b> {s.bankAccount.accountNumber}
                            {s.bankAccount.accountHolder && <span style={{ color: '#475569' }}> (예금주 {s.bankAccount.accountHolder})</span>}
                        </div>
                    )}
                    {s.notes && <div style={{ marginTop: 12, fontSize: 12.5, color: '#64748B', background: '#F8FAFC', borderRadius: 8, padding: '10px 12px' }}>{s.notes}</div>}

                    {/* 확인 영역 — 비회원은 확인·수정 요청만. 정산 완료(잠금)는 회원 치과만 (계약 §1) */}
                    <div style={{ marginTop: 22, textAlign: 'center' }}>
                        {confirmed ? (
                            <div style={{ display: 'inline-block', background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D', borderRadius: 10, padding: '12px 22px', fontSize: 14, fontWeight: 700 }}>
                                ✅ 확인 완료 — {share.confirmedName} ({fmtDate(share.confirmedAt)})
                            </div>
                        ) : (
                            <>
                                {/* 법무 고정 문구 — 확인 버튼 위 (계약 §2) */}
                                <div style={{ maxWidth: 560, margin: '0 auto 10px', fontSize: 12.5, color: '#475569', lineHeight: 1.6, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, padding: '10px 12px', textAlign: 'left' }}>
                                    {CONFIRM_LEGAL_NOTE}
                                </div>
                                {/* 확인자명 인라인 입력 (window.prompt 대체 — 2026-08-26) */}
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <input
                                        value={confirmName}
                                        onChange={(e) => setConfirmName(e.target.value.slice(0, NAME_MAX))}
                                        maxLength={NAME_MAX}
                                        placeholder="확인하시는 분 성함(또는 치과명)"
                                        style={{ padding: '12px 14px', border: '1px solid #E2E8F0', borderRadius: 10, fontSize: 14, minWidth: 220, boxSizing: 'border-box' }}
                                    />
                                    <button onClick={handleConfirm} disabled={confirming || sendingRev} style={{
                                        padding: '13px 26px', border: 'none', borderRadius: 10, background: '#10B981', color: '#fff',
                                        fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                                        opacity: confirming ? 0.6 : 1,
                                    }}>
                                        {confirming ? '처리 중...' : '✓ 내용을 확인했습니다'}
                                    </button>
                                </div>
                                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 8 }}>확인하시면 기공소에 알림이 전달됩니다 (확인은 1회만 가능합니다)</div>
                                {/* 입력칸 안내 (계약 §2) */}
                                <div style={{ maxWidth: 560, margin: '8px auto 0', fontSize: 11.5, color: '#64748B', lineHeight: 1.6, textAlign: 'left' }}>
                                    {inputLegalNote(share.labName)}
                                </div>

                                {/* 수정 요청 (rev1~rev3 칸, 최대 3회) */}
                                <div style={{ maxWidth: 560, margin: '16px auto 0', textAlign: 'left' }}>
                                    {canRevise && !revOpen && (
                                        <div style={{ textAlign: 'center' }}>
                                            <button onClick={() => setRevOpen(true)}
                                                style={{ padding: '9px 18px', background: '#fff', color: '#B45309', border: '1px solid #FCD34D', borderRadius: 8, cursor: 'pointer', fontSize: 13.5, fontWeight: 700 }}>
                                                {`수정 요청${revCount > 0 ? ` (${revCount}/${REV_MAX})` : ''}`}
                                            </button>
                                        </div>
                                    )}
                                    {canRevise && revOpen && (
                                        <div style={{ border: '1px solid #FDE68A', background: '#FFFBEB', borderRadius: 10, padding: 12 }}>
                                            <textarea
                                                value={revText}
                                                onChange={(e) => setRevText(e.target.value.slice(0, REV_TEXT_MAX))}
                                                maxLength={REV_TEXT_MAX}
                                                rows={3}
                                                placeholder={'예: 10/21 김O수 #36 금액 확인 부탁드립니다'}
                                                style={{ width: '100%', padding: '8px 10px', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13.5, resize: 'vertical', boxSizing: 'border-box', background: '#fff' }}
                                            />
                                            {/* 보내는 분 성함 — 수정 요청 상자 안에 따로 (비우면 위 '확인하시는 분 성함' 칸 값, 그것도 비면 이름 없이 전달) */}
                                            <input
                                                value={revName}
                                                onChange={(e) => setRevName(e.target.value.slice(0, NAME_MAX))}
                                                maxLength={NAME_MAX}
                                                placeholder={confirmName.trim() ? `보내는 분 성함 (비우면 '${confirmName.trim()}')` : '보내는 분 성함 (선택 — 비우면 이름 없이 전달)'}
                                                style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: '#fff' }}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 11.5, color: '#92400E' }}>
                                                    {`${revText.length}/${REV_TEXT_MAX}자 · ${REV_MAX - revCount}번 더 보낼 수 있습니다`}
                                                </span>
                                                <span style={{ flex: 1 }} />
                                                <button onClick={() => { setRevOpen(false); }} disabled={sendingRev}
                                                    style={{ padding: '7px 14px', background: '#fff', color: '#475569', border: '1px solid #E2E8F0', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                                                    {'닫기'}
                                                </button>
                                                <button onClick={handleSendRevision} disabled={sendingRev || !revText.trim()}
                                                    style={{ padding: '7px 16px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 8, cursor: sendingRev ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: (sendingRev || !revText.trim()) ? 0.6 : 1 }}>
                                                    {sendingRev ? '보내는 중...' : '보내기'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {!canRevise && revCount >= REV_MAX && (
                                        <div style={{ textAlign: 'center', fontSize: 12, color: '#92400E' }}>
                                            {`수정 요청은 최대 ${REV_MAX}번까지 보낼 수 있습니다. 더 필요하면 기공소에 직접 연락해 주세요.`}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {/* 보낸 수정 요청 (이 링크에서 보낸 것) */}
                        {revisions.length > 0 && (
                            <div style={{ maxWidth: 560, margin: '14px auto 0', textAlign: 'left', fontSize: 12.5, color: '#475569' }}>
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{`보낸 수정 요청 ${revisions.length}건`}</div>
                                {revisions.map((r, i) => (
                                    <div key={i} style={{ padding: '6px 10px', borderLeft: '3px solid #FCD34D', background: '#FFFBEB', borderRadius: 4, marginBottom: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                        {r.text}
                                        <span style={{ marginLeft: 6, fontSize: 11, color: '#94A3B8' }}>
                                            {[r.name || '', fmtDate(r.at)].filter(Boolean).join(' · ')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 가입 유도 배너 */}
                <div style={{ marginTop: 16, background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 14, padding: '18px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: '#3730A3', marginBottom: 4 }}>매달 명세서를 자동으로 받아보세요</div>
                    <div style={{ fontSize: 12.5, color: '#6366F1', marginBottom: 12 }}>
                        덴탈클러스터에 가입하면 {share.labName || '기공소'}의 명세서·세금계산서를 한곳에서 확인하고 정산 확인까지 할 수 있습니다. 치과 회원은 무료로 이용합니다.
                    </div>
                    <button onClick={() => navigate(share.inviteCode ? `/signup?invite=${share.inviteCode}` : '/login')} style={{
                        padding: '10px 24px', border: 'none', borderRadius: 8, background: '#6366F1', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
                    }}>
                        {share.inviteCode ? '초대 코드로 가입하기' : '덴탈클러스터 시작하기'}
                    </button>
                </div>

                <div style={{ textAlign: 'center', marginTop: 18, fontSize: 11.5, color: '#94A3B8' }}>
                    본 문서는 {share.labName || '기공소'}가 덴탈클러스터를 통해 공유한 거래명세서입니다 · 문의 010-2972-3733
                </div>
            </div>
        </div>
    );
}

export default StatementShareView;
